/* Stripe wiring health widget shown on the Admin → Stats tab.
 * Surfaces at-a-glance: API key mode (live/test), webhook secret presence,
 * last webhook received, and recent successful checkouts. Never leaks the
 * actual API key — only presence + prefix mode.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return "—"; }
}

function fmtAmount(amt, ccy) {
  if (amt == null) return "—";
  const cur = (ccy || "gbp").toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: cur }).format(amt);
  } catch { return `${cur} ${amt}`; }
}

function relative(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return null;
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

export default function StripeHealthCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stripe-health");
      setData(data);
    } catch {
      toast.error("Could not load Stripe health");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHealth(); }, []);

  if (!data && loading) {
    return (
      <div className="card-recipe mb-8" data-testid="stripe-health-loading">
        <p className="overline mb-2 text-rf-orange">Stripe wiring</p>
        <p className="text-sm text-rf-ink-soft">Checking…</p>
      </div>
    );
  }
  if (!data) return null;

  const keyMode = data.stripe_key_mode; // "live" | "test" | "unknown" | null
  const keyStatus = data.stripe_key_status; // "ok" | "expired_or_invalid" | "unreachable" | "error" | "unknown"
  const keyChipClass =
    !data.stripe_key_present
      ? "!bg-rose-500/20 !text-rose-800"
      : keyStatus === "expired_or_invalid"
      ? "!bg-rose-500/20 !text-rose-800"
      : keyStatus === "unreachable" || keyStatus === "error"
      ? "!bg-amber-400/20 !text-amber-900"
      : keyMode === "live"
      ? "!bg-emerald-500/20 !text-emerald-800"
      : keyMode === "test"
      ? "!bg-amber-400/20 !text-amber-900"
      : "!bg-rose-500/20 !text-rose-800";
  const keyLabel = !data.stripe_key_present
    ? "MISSING"
    : keyStatus === "expired_or_invalid"
    ? "EXPIRED / INVALID"
    : keyStatus === "unreachable"
    ? "UNREACHABLE"
    : keyStatus === "error"
    ? "STRIPE ERROR"
    : keyMode === "live"
    ? "LIVE — OK"
    : keyMode === "test"
    ? "TEST MODE"
    : "UNKNOWN PREFIX";
  const webhookChipClass = data.webhook_secret_present
    ? "!bg-emerald-500/20 !text-emerald-800"
    : "!bg-amber-400/20 !text-amber-900";
  const rel = relative(data.last_webhook_at);

  return (
    <div className="card-recipe mb-8" data-testid="stripe-health-card">
      <div className="flex items-center justify-between mb-3">
        <p className="overline text-rf-orange">Stripe wiring</p>
        <button
          data-testid="stripe-health-refresh"
          onClick={fetchHealth}
          disabled={loading}
          className="text-xs font-bold text-rf-orange hover:underline disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="p-4 rounded-xl bg-rf-cream-alt" data-testid="stripe-health-key">
          <p className="text-[11px] font-bold text-rf-ink-soft uppercase tracking-wider mb-1">API key</p>
          <p className="font-display font-black text-lg text-rf-ink">
            <span className={`chip ${keyChipClass}`}>{keyLabel}</span>
          </p>
        </div>
        <div className="p-4 rounded-xl bg-rf-cream-alt" data-testid="stripe-health-webhook-secret">
          <p className="text-[11px] font-bold text-rf-ink-soft uppercase tracking-wider mb-1">Webhook secret</p>
          <p className="font-display font-black text-lg text-rf-ink">
            <span className={`chip ${webhookChipClass}`}>
              {data.webhook_secret_present ? "SET" : "NOT SET"}
            </span>
          </p>
        </div>
        <div className="p-4 rounded-xl bg-rf-cream-alt" data-testid="stripe-health-last-webhook">
          <p className="text-[11px] font-bold text-rf-ink-soft uppercase tracking-wider mb-1">Last webhook</p>
          <p className="font-display font-black text-lg text-rf-ink leading-tight">
            {rel || "None yet"}
          </p>
          {data.last_webhook_at && (
            <p className="text-[11px] text-rf-ink-soft mt-1">{fmtDate(data.last_webhook_at)}</p>
          )}
        </div>
        <div className="p-4 rounded-xl bg-rf-cream-alt" data-testid="stripe-health-paid-24h">
          <p className="text-[11px] font-bold text-rf-ink-soft uppercase tracking-wider mb-1">Paid (24 h)</p>
          <p className="font-display font-black text-2xl text-rf-ink">
            {data.paid_24h ?? 0}
            <span className="text-sm text-rf-ink-soft font-bold ml-2">/ {data.checkouts_24h ?? 0} started</span>
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 text-xs text-rf-ink-soft mb-5">
        <div><b className="text-rf-ink">{data.webhooks_24h ?? 0}</b> webhook{data.webhooks_24h === 1 ? "" : "s"} in 24 h</div>
        <div><b className="text-rf-ink">{data.webhooks_7d ?? 0}</b> webhook{data.webhooks_7d === 1 ? "" : "s"} in 7 d</div>
        <div><b className="text-rf-ink">{data.paid_7d ?? 0}</b> paid checkout{data.paid_7d === 1 ? "" : "s"} in 7 d</div>
      </div>

      {(!data.stripe_key_present || !data.webhook_secret_present || keyMode === "test" || keyStatus === "expired_or_invalid" || keyStatus === "unreachable" || keyStatus === "error") && (
        <div className="p-3 rounded-xl bg-amber-100 border border-amber-300 text-xs text-amber-900 mb-5" data-testid="stripe-health-warning">
          {!data.stripe_key_present && (<div>⚠ <b>STRIPE_API_KEY missing</b> — set it in the pod .env and restart the backend.</div>)}
          {data.stripe_key_present && keyStatus === "expired_or_invalid" && (
            <div>
              ⛔ <b>Stripe rejected the API key.</b> {data.stripe_key_status_detail || "Likely expired or revoked."}
              <br />
              <span className="italic">Fix:</span> Log into <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-bold">Stripe Dashboard → Developers → API keys</a>, copy the current Secret key, replace <code>STRIPE_API_KEY</code> in the pod .env, then restart the backend. Users are currently seeing a &ldquo;Payments temporarily unavailable&rdquo; error on checkout.
            </div>
          )}
          {keyStatus === "unreachable" && (<div>⚠ Couldn&apos;t reach Stripe from this pod: <code>{data.stripe_key_status_detail}</code>. Possibly a transient network glitch — retry.</div>)}
          {keyStatus === "error" && (<div>⚠ Stripe returned an unexpected status: <code>{data.stripe_key_status_detail}</code>.</div>)}
          {data.stripe_key_present && keyMode === "test" && (<div>ℹ Stripe is in <b>TEST mode</b> — real cards won&apos;t be charged. Swap to a <code>sk_live_</code> key when ready.</div>)}
          {data.stripe_key_present && keyMode === "unknown" && (<div>⚠ Stripe key doesn&apos;t start with <code>sk_live_</code> or <code>sk_test_</code>. Double-check it&apos;s a valid Secret Key.</div>)}
          {!data.webhook_secret_present && (<div>ℹ <b>STRIPE_WEBHOOK_SECRET is not set.</b> Polling still credits users on the success page, but adding the secret hardens webhook signature verification.</div>)}
        </div>
      )}

      <div>
        <p className="text-[11px] font-bold text-rf-ink-soft uppercase tracking-wider mb-2">Recent successful checkouts</p>
        {data.recent_paid?.length ? (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-bold text-rf-ink-soft uppercase tracking-wider">
                  <th className="px-2 py-2">When</th>
                  <th className="px-2 py-2">User</th>
                  <th className="px-2 py-2">Type</th>
                  <th className="px-2 py-2">Amount</th>
                  <th className="px-2 py-2">Via</th>
                </tr>
              </thead>
              <tbody data-testid="stripe-health-recent-body">
                {data.recent_paid.map((r, i) => (
                  <tr key={i} className="border-t border-rf-ink/5">
                    <td className="px-2 py-2 text-rf-ink-soft">{fmtDate(r.credited_at)}</td>
                    <td className="px-2 py-2 font-mono text-xs text-rf-ink">{r.user_email || "—"}</td>
                    <td className="px-2 py-2">
                      <span className="chip !bg-rf-cream-alt !text-rf-ink text-[11px]">
                        {r.kind === "gift" ? "gift" : (r.package_id || "sub")}
                      </span>
                      {r.discount_code && (
                        <span className="chip !bg-rf-orange/15 !text-rf-orange text-[11px] ml-1">{r.discount_code}</span>
                      )}
                    </td>
                    <td className="px-2 py-2 font-bold text-rf-ink">{fmtAmount(r.amount, r.currency)}</td>
                    <td className="px-2 py-2 text-xs text-rf-ink-soft">{r.credited_via || "poll"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-rf-ink-soft" data-testid="stripe-health-recent-empty">
            No successful checkouts yet. Once someone pays via Stripe, the last 5 will appear here.
          </p>
        )}
      </div>
    </div>
  );
}
